import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ShieldAlert, Download } from "lucide-react";
import { complianceApi } from "../../services/complianceApi";
import { StatusBadge } from "../../components/StatusBadge";
import { useLocalization } from "../../hooks/useLocalization";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";

const ACTION_STATUSES = ["NOT_STARTED", "IN_PROGRESS", "READY_TO_SUBMIT", "SUBMITTED", "ACCEPTED"];
const ACTION_STATUS_COLORS = {
  NOT_STARTED: "bg-gray-100 text-gray-600 border-gray-300",
  IN_PROGRESS: "bg-blue-100 text-blue-700 border-blue-300",
  READY_TO_SUBMIT: "bg-amber-100 text-amber-800 border-amber-300",
  SUBMITTED: "bg-purple-100 text-purple-700 border-purple-300",
  ACCEPTED: "bg-emerald-100 text-emerald-700 border-emerald-300",
};

export default function ComplianceDashboardPage() {
  const { t } = useLocalization();
  const { canReviewLaborCompliance } = usePermissions();
  const [sources, setSources] = useState([]);
  const [actions, setActions] = useState([]);
  const [notes, setNotes] = useState({});
  const [employees, setEmployees] = useState([]);
  const [exportForm, setExportForm] = useState({ employee_id: "", payroll_month: "" });
  const [exporting, setExporting] = useState(false);
  const [emailReadiness, setEmailReadiness] = useState(null);
  const [fullExports, setFullExports] = useState([]);
  const [fullExportForm, setFullExportForm] = useState({ date_range_start: "", date_range_end: "", mfa_code: "" });
  const [fullExporting, setFullExporting] = useState(false);

  const load = () => {
    complianceApi.listSources().then((r) => setSources(r.data));
    complianceApi.listCorrectiveActions().then((r) => setActions(r.data));
    if (canReviewLaborCompliance) {
      complianceApi.listEmployeesForExport().then((r) => setEmployees(r.data)).catch(() => {});
      complianceApi.emailReadiness().then((r) => setEmailReadiness(r.data)).catch(() => {});
      complianceApi.listFullExports().then((r) => setFullExports(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, []);

  const handleFullExport = async () => {
    if (!fullExportForm.date_range_start || !fullExportForm.date_range_end || !fullExportForm.mfa_code) {
      toast.error("Date range and a current MFA code are required"); return;
    }
    setFullExporting(true);
    try {
      await complianceApi.generateFullExport(fullExportForm);
      toast.success("Labor Office export generated");
      setFullExportForm({ ...fullExportForm, mfa_code: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail?.blockers?.join("; ") || err.response?.data?.detail)); }
    finally { setFullExporting(false); }
  };

  const downloadFullExport = async (id) => {
    try {
      const { data } = await complianceApi.downloadFullExport(id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `labor_office_export_${id}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submitFullExport = async (id) => {
    try { await complianceApi.submitFullExport(id); toast.success("Marked as submitted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleExport = async () => {
    if (!exportForm.employee_id || !exportForm.payroll_month) { toast.error("Choose an employee and payroll month"); return; }
    setExporting(true);
    try {
      const { data } = await complianceApi.exportInspectionPackage(exportForm.employee_id, exportForm.payroll_month);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `inspection_export_${exportForm.employee_id}_${exportForm.payroll_month}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Inspection export downloaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const handleStatusChange = async (action, status) => {
    try {
      await complianceApi.updateCorrectiveAction(action.id, { status, notes: notes[action.id] ?? action.notes });
      toast.success(t("compliance.statusUpdated"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="compliance-dashboard-page">
      <PageHelpBanner pageKey="compliance-dashboard" title="How to use this page">
        <p>Corrective action cards below need a status update and notes as each labor-office finding is worked through. The Sources table lists every legal requirement this system tracks and whether it's implemented yet. Scroll down for email readiness and Labor Office inspection export tools (require Compliance Manager access).</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-1">{t("compliance.title")}</h2>
      <p className="text-sm text-gray-500 mb-6">{t("compliance.subtitle")}</p>

      <div className="bg-white rounded-xl border border-red-200 p-5 mb-8" data-testid="corrective-actions-panel">
        <div className="flex items-center gap-2 mb-4">
          <ShieldAlert size={18} className="text-red-500" />
          <h3 className="text-sm font-semibold text-gray-900">{t("compliance.correctiveActionsPanel")}</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {actions.map((a) => (
            <div key={a.id} className="border border-gray-200 rounded-lg p-4" data-testid={`corrective-action-${a.code}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-mono text-gray-500">{a.code}</span>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${ACTION_STATUS_COLORS[a.status] || ACTION_STATUS_COLORS.NOT_STARTED}`}>
                  {a.status.replace(/_/g, " ")}
                </span>
              </div>
              <p className="font-semibold text-gray-900 text-sm mb-1">{a.title}</p>
              <p className="text-xs text-gray-500 mb-2">{a.legal_provision}</p>
              <p className="text-xs text-gray-600 mb-1"><span className="font-semibold">{t("compliance.finding")}:</span> {a.finding}</p>
              <p className="text-xs text-gray-600 mb-1"><span className="font-semibold">{t("compliance.required")}:</span> {a.required_correction}</p>
              <p className="text-xs font-semibold text-red-600 mb-3">{t("compliance.deadline")}: {a.deadline}</p>

              {canReviewLaborCompliance && (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <textarea
                    data-testid={`corrective-action-notes-${a.code}`}
                    placeholder={t("compliance.notes")}
                    defaultValue={a.notes || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 text-xs"
                    rows={2}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {ACTION_STATUSES.map((status) => (
                      <button
                        key={status}
                        data-testid={`corrective-action-set-${a.code}-${status}`}
                        disabled={status === a.status}
                        onClick={() => handleStatusChange(a, status)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all active:scale-95 disabled:opacity-40 ${
                          status === a.status ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {status.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid="compliance-sources-panel">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <FileText size={18} className="text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-900">{t("compliance.sourcesPanel")}</h3>
        </div>
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">{t("compliance.source")}</th>
              <th className="px-4 py-2 font-medium">{t("compliance.type")}</th>
              <th className="px-4 py-2 font-medium">{t("compliance.systemModule")}</th>
              <th className="px-4 py-2 font-medium">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id} className="border-t border-gray-100" data-testid={`source-row-${s.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{s.source_name}</p>
                  <p className="text-xs text-gray-500">{s.legal_provision}</p>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{s.source_type}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{s.system_module}</td>
                <td className="px-4 py-3"><StatusBadge status={s.implementation_status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canReviewLaborCompliance && emailReadiness && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mt-8" data-testid="email-readiness-panel">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Email Production Readiness (Mandrill)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(emailReadiness.checks).map(([key, ok]) => (
              <div key={key} className={`text-xs font-semibold px-2 py-1.5 rounded-lg border ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-50 text-gray-500 border-gray-200"}`} data-testid={`email-readiness-check-${key}`}>
                {ok ? "✓" : "○"} {key.replaceAll("_", " ")}
              </div>
            ))}
          </div>
          <p className={`text-xs font-semibold mt-3 ${emailReadiness.ready ? "text-emerald-600" : "text-amber-700"}`} data-testid="email-readiness-status">
            {emailReadiness.ready ? "Ready to send real emails" : `Not ready - blocked by: ${emailReadiness.blockers.join(", ")}`}
          </p>
        </div>
      )}

      {canReviewLaborCompliance && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mt-8" data-testid="inspection-export-panel">
          <div className="flex items-center gap-2 mb-4">
            <Download size={18} className="text-gray-700" />
            <h3 className="text-sm font-semibold text-gray-900">Labor Office Inspection Export</h3>
          </div>
          <p className="text-xs text-gray-500 mb-3">Builds a ZIP evidence package (timekeeping, payroll, leave, medical, training) for one employee's payroll month. Missing evidence is always labeled, never fabricated.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select data-testid="export-employee-select" value={exportForm.employee_id} onChange={(e) => setExportForm({ ...exportForm, employee_id: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
              <option value="">Employee...</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
            <input data-testid="export-payroll-month-input" type="month" value={exportForm.payroll_month} onChange={(e) => setExportForm({ ...exportForm, payroll_month: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <button data-testid="download-inspection-export-btn" disabled={exporting} onClick={handleExport} className="px-4 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">
              {exporting ? "Building..." : "Download ZIP"}
            </button>
          </div>
        </div>
      )}
      {canReviewLaborCompliance && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mt-8" data-testid="full-inspection-export-panel">
          <div className="flex items-center gap-2 mb-4">
            <Download size={18} className="text-gray-700" />
            <h3 className="text-sm font-semibold text-gray-900">Full Labor Office Inspection Export (13 sections)</h3>
          </div>
          <p className="text-xs text-gray-500 mb-3">Scoped by exact date range across all employees/stores. Requires a fresh MFA code every time. Never fabricates evidence - missing or not-yet-built sections are always labeled.</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input data-testid="full-export-start-date" type="date" value={fullExportForm.date_range_start} onChange={(e) => setFullExportForm({ ...fullExportForm, date_range_start: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <input data-testid="full-export-end-date" type="date" value={fullExportForm.date_range_end} onChange={(e) => setFullExportForm({ ...fullExportForm, date_range_end: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <input data-testid="full-export-mfa-code" value={fullExportForm.mfa_code} onChange={(e) => setFullExportForm({ ...fullExportForm, mfa_code: e.target.value })} placeholder="Current MFA code" className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
            <button data-testid="generate-full-export-btn" disabled={fullExporting} onClick={handleFullExport} className="px-4 py-2 rounded-lg text-xs font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">
              {fullExporting ? "Building..." : "Generate Export"}
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {fullExports.map((e) => (
              <div key={e.id} className="border border-gray-100 rounded-lg p-3 flex items-center justify-between" data-testid={`full-export-row-${e.id}`}>
                <div>
                  <p className="text-xs font-semibold text-gray-800">{e.date_range_start} to {e.date_range_end} - {e.status}</p>
                  <p className="text-xs text-gray-500">Submission: {e.submission_status} - {e.missing_evidence_report?.length || 0} section(s) flagged</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`download-full-export-btn-${e.id}`} onClick={() => downloadFullExport(e.id)} className="text-xs font-semibold text-blue-600 hover:underline">Download</button>
                  {e.submission_status === "NOT_SUBMITTED" && (
                    <button data-testid={`submit-full-export-btn-${e.id}`} onClick={() => submitFullExport(e.id)} className="text-xs font-semibold text-emerald-600 hover:underline">Mark Submitted</button>
                  )}
                </div>
              </div>
            ))}
            {fullExports.length === 0 && <p className="text-xs text-gray-500" data-testid="no-full-exports-message">No full exports generated yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
